// © Microsoft Corporation. All rights reserved.

import { CombinedObjectTelemetryContract, ObjectTelemetryContractInternal } from "../types/TelemetryInternalContract";
import {
    DeepPartial, ObjectTelemetryContract, TelemetryAttrName, TelemetryMetadata, TelemetryObjectBase,
    TrackingOptions, TelemetryMetadataAttribute
} from "@msnews/telemetry-contracts";
import { merge, values } from "lodash-es";

/**
 * Object metadata class which combination of external contract and internal data
 * @class TelemetryObject
 */
export class TelemetryObject implements TelemetryObjectBase {
    /**
     * Root object.
     */
    private static root: TelemetryObject = new TelemetryObject(
        { name: "root" },
        null,
        true
    );

    /**
     * Default values
     */
    private static defaultValue: DeepPartial<ObjectTelemetryContract> = {};

    /**
     * Mapping between object id and object itself.
     */
    private static objectIdMapper: { [key: string]: TelemetryObject } = {};

    /**
     * Last id.
     */
    private static lastId = 0;

    /**
     * Flag for identifying whether object already is captured or not.
     */
    public isCaptured?: boolean;

    /**
     * Flag for identifying whether object is visible or not.
     */
    public isVisible?: boolean;

    /**
     * Id of current object.
     */
    public id: number;

    /**
     * Full id.
     */
    public fullId: string;

    /**
     * Reference to parent object.
     */
    public readonly parent: TelemetryObject;

    /**
     * External object contract.
     */
    public contract: ObjectTelemetryContract;

    /**
     * Ordinal of the object.
     */
    private ordinal: number;

    /**
     * Last ordinal of its children.
     */
    private ordinalCount: number;

    /**
     * Contract getter.
     * @returns Combined contract between internal and external.
     */
    public get combinedContract(): CombinedObjectTelemetryContract {
        return merge({}, TelemetryObject.defaultValue, this.contract, this.internalContract);
    }

    /**
     * Internal contract getter.
     * @returns Internal contract.
     */
    private get internalContract(): ObjectTelemetryContractInternal {
        const { lineage, lineageOrdinal } = TelemetryObject.getLineage(this);
        const contract = {
            placement: {
                id: this.id,
                lineage: lineage,
                lineageOrdinal: lineageOrdinal
            }
        } as ObjectTelemetryContractInternal;

        if (this.parent && this.parent.id && this.parent.id !== 0) {
            contract.placement.parentId = this.parent.id;
            contract.placement.ordinal = this.ordinal;
        }

        return contract;
    }

    constructor(contract: ObjectTelemetryContract, parent?: TelemetryObject, isRoot?: boolean) {
        this.contract = contract;
        this.ordinal = 0;
        this.ordinalCount = 0;

        if (!isRoot) {
            if (parent) {
                this.parent = parent;
            } else {
                this.parent = TelemetryObject.root;
            }

            this.id = this.createObjectId();
            this.setOrdinal();
        }
    }

    /**
     * Get root object
     * @returns Telemetry root object.
     */
    public static getRoot(): TelemetryObject {
        return TelemetryObject.root;
    }

    /**
     * Get telemetry object by id.
     * @param fullId full id.
     * @returns Telemetry object.
     */
    public static getItemById(fullId: string): TelemetryObject {
        return this.objectIdMapper[fullId];
    }

    /**
     * Get all telemetry objects.
     * @returns Telemetry objects.
     */
    public static getItems(): TelemetryObject[] {
        return values(this.objectIdMapper);
    }

    /**
     * Reset id to be back to 0.
     */
    public static resetIdTracker(): void {
        this.objectIdMapper = {};
        this.lastId = 0;
        TelemetryObject.root.resetOrdinal();
    }

    /**
     * Get telemetry object from an element.
     * @param element The element.
     * @returns Telemetry object.
     */
    public static getObject(element: Element): TelemetryObject {
        const metadataObject = this.parseTelemetryAttribute(element);
        return metadataObject && TelemetryObject.getItemById(metadataObject.i);
    }

    public static parseTelemetryAttribute(element: Element): TelemetryMetadata {
        const metadataAttribute = element && element.getAttribute && element.getAttribute(TelemetryAttrName);
        return metadataAttribute && JSON.parse(metadataAttribute) as TelemetryMetadata;
    }

    /**
     * Get lineage and lineageOrdinal strings.
     * Lineage is concatenation of telemetry object's name of the current object up to the root. It basically tells the placement of the object in the page.
     * Lineage ordinal is concatenation of ordinal. It tells which position of each object up to root in the hierachy is.
     * Lineage can be duplicated since object name can be the same in some cases e.g. slideshow. Lineage ordinal is used to distinguish that.
     * @params obj Target telemetry object.
     * @returns Lineage and lineageOrdinal strings.
     */
    public static getLineage(obj: TelemetryObject, delimeter = ">", includeRootElement = false): any {
        if (obj.parent && obj.parent !== TelemetryObject.root) {
            let { lineage, lineageOrdinal } = this.getLineage(obj.parent, delimeter, includeRootElement);
            lineage += delimeter + obj.contract.name;
            const newLineageOrdinal = (lineageOrdinal ? lineageOrdinal + delimeter : "") + obj.ordinal;
            return { lineage, lineageOrdinal: newLineageOrdinal };
        }

        if (obj.parent && obj.parent === TelemetryObject.root && includeRootElement) {
            const outputObj: any = { lineage: obj.contract.name };
            outputObj.lineageOrdinal = "" + obj.ordinal;
            return outputObj;
        }

        return { lineage: obj.contract.name };
    }

    /**
     * Add a new telemetry object as a child.
     * @param contract Object telemetry contract.
     * @returns New telemetry object.
     */
    public addChild(contract: ObjectTelemetryContract): TelemetryObject {
        return new TelemetryObject(contract, this);
    }

    /**
     * Add or update a child telemetry object.
     * @param contract Object telemetry contract.
     * @param savedChildObject Existing child telemetry object (if exist).
     * @returns New telemetry object.
     */
    public addOrUpdateChild(contract: ObjectTelemetryContract, savedChildObject?: TelemetryObject): TelemetryObject {
        // Check both childObject and id in case current object already get soft deleted.
        if (savedChildObject && this.id) {
            savedChildObject.contract = contract;
            return savedChildObject;
        }

        return new TelemetryObject(contract, this);
    }

    /**
     * Builds telemetry metadata object
     * @param options - tracking options
     */
    public buildMetadata(options?: TrackingOptions): TelemetryMetadata {
        this.mapObjectId();

        const attr = {} as TelemetryMetadata;
        attr.i = this.fullId;

        const action = (options && options.action) || this.contract.action;
        if (action !== undefined && action !== null) {
            attr.a = action;
        }

        const behavior = (options && options.behavior) || this.contract.behavior;
        if (behavior !== undefined && behavior !== null) {
            attr.b = behavior;
        }

        return attr;
    }

    /**
     * Produces a string version of the telemetry object's metadata
     * @param options Tracking options
     * @returns Stringified json telemetry object
     */
    public getMetadataTag(options?: TrackingOptions): string {
        return JSON.stringify(this.buildMetadata(options));
    }

    /**
     * Render telemetry attribute.
     * @param options Tracking options.
     * @returns Telemetry attribute.
     */
    public render(options?: TrackingOptions): TelemetryMetadataAttribute {
        return { [TelemetryAttrName]: this.getMetadataTag(options) };
    }

    /**
     * Reset ordinal.
     */
    public resetOrdinal(): void {
        this.ordinalCount = 0;
    }

    /**
     * Set ordinal.
     */
    public setOrdinal(): void {
        if (this.ordinal === 0) {
            this.parent.ordinalCount++;
            this.ordinal = this.parent.ordinalCount;
            this.ordinalCount = 0;
        }
    }

    /**
     * Soft delete. Remove all references to the current object to allow gabage colletor to clear it out.
     */
    public delete(): void {
        const children = this.getChildren();
        for (let i = 0; i < children.length; i++) {
            const child = children[i];
            child.delete();
        }

        // Remove reference to this object to allow gabage collector to collect.
        delete TelemetryObject.objectIdMapper[this.fullId];
        // Reset ordinal of all children under the same parent because order will change after the object get deleted.
        this.parent.resetOrdinal();
    }

    /**
     * Create object id.
     * @param Object id.
     */
    private createObjectId(): number {
        return ++TelemetryObject.lastId;
    }

    /**
     * Map between object and fullId
     */
    private mapObjectId(): void {
        if (this.contract && this.contract.overrideId) {
            this.fullId = this.contract.overrideId;
        } else if (this.fullId) {
            return;
        } else {
            const { _, lineageOrdinal } = TelemetryObject.getLineage(this, "|", true);
            this.fullId = lineageOrdinal;
        }

        TelemetryObject.objectIdMapper[this.fullId] = this;
    }

    /**
     * Check whether this is parent of the target object.
     * @param obj Target object.
     * @returns true if this is the parent. Otherwise, false.
     */
    private isParentOf(obj: TelemetryObject): boolean {
        return obj.parent === this;
    }

    /**
     * Get all the children.
     */
    private getChildren(): TelemetryObject[] {
        const children = [];
        if (TelemetryObject.objectIdMapper) {
            Object.keys(TelemetryObject.objectIdMapper).forEach((key) => {
                const obj = TelemetryObject.objectIdMapper[key];
                if (this.isParentOf(obj)) {
                    children.push(obj);
                }
            });
        }

        return children;
    }
}
